import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LoginActivityAlert from "@/components/LoginActivityAlert";
import { Card, CardContent } from "@/components/ui/card";
import {
  Banknote, ShoppingCart, FileText, Users, Phone, Target, MapPin, ChartBar, Wallet, Calendar, ListChecks, Building2,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, tone = "text-primary", suffix = "" }) {
  return (
    <div className="surface-card p-5" data-testid={`sales-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between"><span className="label-overline">{label}</span><Icon className={`w-4 h-4 ${tone}`} /></div>
      <div className="font-display text-2xl sm:text-3xl font-black mt-3">{value ?? "—"}{suffix && <span className="text-sm font-bold text-muted-foreground ml-1">{suffix}</span>}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/sales/kpis").then(({ data }) => setK(data)); }, []);

  const isManager = user?.role === "SALES_MANAGER" || user?.role === "ORG_ADMIN";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8" data-testid="sales-dashboard">
      <div>
        <div className="label-overline">{isManager ? "Sales operations" : "My territory"}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">{t("dashboards.sales.welcome", { name: user?.full_name?.split(" ")[0] || "there" })}</h1>
        <p className="text-muted-foreground mt-2">{isManager ? t("dashboards.sales.subtitleManager") : t("dashboards.sales.subtitleExec")}</p>
      </div>

      <LoginActivityAlert />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Sales today" value={`₹${(k?.sales_today || 0).toLocaleString()}`} icon={Banknote} tone="text-emerald-600" />
        <Kpi label="Monthly sales" value={`₹${(k?.sales_month || 0).toLocaleString()}`} icon={Banknote} tone="text-emerald-700" />
        <Kpi label="Target achievement" value={k?.target_achievement_pct ?? 0} suffix="%" icon={Target} tone="text-violet-700" />
        <Kpi label="Collection pending" value={`₹${(k?.collection_pending || 0).toLocaleString()}`} icon={Wallet} tone="text-amber-700" />
        <Kpi label="Pending quotations" value={k?.pending_quotations} icon={FileText} />
        <Kpi label="Awaiting approval" value={k?.awaiting_approval} icon={FileText} tone="text-amber-600" />
        <Kpi label="Confirmed orders" value={k?.confirmed_orders} icon={ShoppingCart} tone="text-emerald-700" />
        <Kpi label="Pending orders" value={k?.pending_orders} icon={ShoppingCart} tone="text-amber-700" />
        <Kpi label="Leads assigned" value={k?.leads_assigned} icon={Users} />
        <Kpi label="New leads today" value={k?.new_leads_today} icon={Users} tone="text-accent" />
        <Kpi label="Follow-ups due today" value={k?.followups_due_today} icon={Phone} tone="text-amber-700" />
        <Kpi label="Dealer orders (MTD)" value={k?.dealer_orders} icon={Building2} />
        <Kpi label="Customer visits (MTD)" value={k?.customer_visits} icon={MapPin} />
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3"><ChartBar className="w-4 h-4 text-primary" /><h2 className="font-display text-xl font-bold">Daily sales · monthly revenue · top dealers</h2></div>
          <p className="text-sm text-muted-foreground">Charts will render here as soon as you have 7+ days of orders. For now, click into <span className="font-mono">Orders</span> for the full transactional view.</p>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <Card><CardContent className="p-4 flex items-center gap-3"><Calendar className="w-4 h-4 text-primary" /> <div><div className="font-semibold">Today’s schedule</div><div className="text-muted-foreground text-xs">Visits, follow-ups and meetings — see Visits.</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><ListChecks className="w-4 h-4 text-primary" /> <div><div className="font-semibold">Pending tasks</div><div className="text-muted-foreground text-xs">Your open tasks live under Tasks.</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><ShoppingCart className="w-4 h-4 text-primary" /> <div><div className="font-semibold">Recent orders</div><div className="text-muted-foreground text-xs">Last 5 orders are visible on Orders.</div></div></CardContent></Card>
      </div>
    </div>
  );
}
